import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { createMigrate, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import type { ViewSlug } from '~/types/components';
import type {
  Campaign,
  Call,
  CallCampaign,
  Contact,
  Email,
  EmailCampaign,
  Group,
  Sms,
  SmsCampaign,
} from '~/types/models';
import { restSliceMigrations } from '~/types/states';
import type { INestedRestSlice, IRestSlice, IRestIndexParams } from '~/types/states';
import type { StringKeys } from '~/types/utils';

import {
  createCallsSlice,
  createContactsSlice,
  createEmailsSlice,
  createSmsSlice,
} from '../nestedRestSlice/slice';

export const nestedContactsSlice = createContactsSlice('campaigns');
export const nestedCallsSlice = createCallsSlice('campaigns');
export const nestedEmailsSlice = createEmailsSlice('campaigns');
export const nestedSmsSlice = createSmsSlice('campaigns');

const initialState: IRestSlice<Campaign, keyof CallCampaign | keyof EmailCampaign | keyof SmsCampaign> & {
  calls: INestedRestSlice<Call>;
  contacts: INestedRestSlice<Contact>;
  emails: INestedRestSlice<Email>;
  sms: INestedRestSlice<Sms>;
} = {
  columns: {
    index: ['type', 'sequence', 'status', 'name', 'description'],
    single: ['type', 'sequence', 'status', 'name', 'description', 'script', 'defaultCallDelay', 'canLeaveMessage', 'maxTries', 'contactsCount', 'callsCount'],
  },
  data: [],
  error: null,
  filters: {},
  initialItem: null,
  item: null,
  loading: true,
  page: 1,
  pageSize: 10,
  sortBy: '-sequence',
  total: null,
  totalPages: null,
  with: [],
  calls: nestedCallsSlice.state,
  contacts: nestedContactsSlice.state,
  emails: nestedEmailsSlice.state,
  sms: nestedSmsSlice.state,
};

export const campaignsSlice = createSlice({
  name: 'campaigns',
  initialState,
  reducers: {
    addUsersFromGroup: (_state, _action: PayloadAction<Group>) => {},
    create: (_state, _action: PayloadAction<Partial<Campaign>>) => {},
    destroy: (_state, _action: PayloadAction<Campaign>) => {},
    index: (_state, _action: PayloadAction<IRestIndexParams<Campaign>>) => {},
    reset: (state) => {
      state.data.splice(0, state.data.length);
      state.loading = false;
      state.pageSize = 10;
      state.page = 1;
      state.filters = {};
    },
    retrieve: (_state, _action: PayloadAction<{ id: Campaign['id']; params?: IRestIndexParams<Campaign> }>) => {},
    sendUnsentEmails: (_state, _action: PayloadAction<Campaign['id']>) => {},
    sendUnsentSms: (_state, _action: PayloadAction<Campaign['id']>) => {},
    setColumns(
      state,
      { payload: { view, columns } }: PayloadAction<{ view: ViewSlug; columns: Array<StringKeys<Campaign>> }>,
    ) {
      state.columns[view].splice(0, state.columns[view].length, ...columns);

      if (state.columns[view].length === 0) {
        state.columns[view].splice(0, state.columns[view].length, ...initialState.columns[view]);
      }
    },
    setData(state, { payload }) {
      state.data.splice(0, state.data.length, ...payload);
    },
    setError(state, { payload }) {
      state.error = payload;
    },
    setFilter: (
      state,
      action: PayloadAction<{
        key: keyof Campaign;
        value: string | number | string[];
      }>,
    ) => {
      state.filters[action.payload.key] = action.payload.value;
    },
    setFilters: (state, { payload }) => {
      Object.keys(payload).forEach((k) => {
        if (payload[k] === null) {
          delete state.filters[k as keyof Campaign];
        } else {
          state.filters[k as keyof Campaign] = payload[k];
        }
      });
    },
    setInitialItem: (state, { payload }) => {
      state.initialItem = payload;
    },
    setItem: (state, { payload }) => {
      state.item = payload;
    },
    setLoading: (state, { payload }) => {
      state.loading = payload;
    },
    setPage: (state, { payload }) => {
      state.page = payload;
    },
    setPageSize: (state, { payload }) => {
      state.pageSize = payload;
    },
    setParams: (_state, _action: PayloadAction<IRestIndexParams<Campaign>>) => {},
    setSortBy: (state, { payload }) => {
      state.sortBy = payload;
    },
    setTotal: (state, { payload }) => {
      state.total = payload;
    },
    setTotalPages: (state, { payload }) => {
      state.totalPages = payload;
    },
    setWith: (state, { payload }: PayloadAction<string[]>) => {
      state.with = payload;
    },
    unsetFilter: (state, action: PayloadAction<keyof Campaign>) => {
      delete state.filters[action.payload];
    },
    update: (_state, _action: PayloadAction<Partial<Campaign>>) => {},
  },
  extraReducers: (builder) => {
    builder.addDefaultCase((state, action) => {
      return {
        ...state,
        calls: nestedCallsSlice.reducer(state.calls, action),
        contacts: nestedContactsSlice.reducer(state.contacts, action),
        emails: nestedEmailsSlice.reducer(state.emails, action),
        sms: nestedSmsSlice.reducer(state.sms, action),
      };
    });
  },
});

const { actions, getInitialState, reducer: campaignsReducer } = campaignsSlice;

const reducer = persistReducer(
  {
    key: 'CSN_Pointage:Campaigns',
    migrate: createMigrate(restSliceMigrations),
    storage,
    version: 0,
    whitelist: ['columns', 'filters', 'page', 'pageSize', 'sortBy', 'with'],
  },
  campaignsReducer,
);

export { actions, getInitialState, reducer };
